import type { MetricSummary } from '../lib/api'

/**
 * Цвет столбиков нельзя собирать строкой вида `bg-${tint}`: Tailwind 4 ищет
 * классы по исходникам как обычный текст и молча не сгенерирует ни одного.
 * Поэтому имя токена с сервера превращается в готовый класс через таблицу.
 */
const TINT_CLASS: Record<string, string> = {
  move: 'bg-move',
  exercise: 'bg-exercise',
  stand: 'bg-stand',
  'sys-blue': 'bg-sys-blue',
  'sys-orange': 'bg-sys-orange',
  'sys-red': 'bg-sys-red',
  'sys-green': 'bg-sys-green',
  'sys-yellow': 'bg-sys-yellow',
  'sys-purple': 'bg-sys-purple',
}

const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']

/**
 * Неделя столбиками. Пустой день остаётся видимой пустой колонкой — иначе
 * пропуск выглядит как ноль, и график врёт ровно там, где важнее всего.
 */
export function Sparkline({
  days,
  tint,
  height = 34,
}: {
  days: MetricSummary['days']
  tint: string
  height?: number
}) {
  // Дни приходят от сегодня назад — рисуем слева направо, как читаем.
  const week = [...days.slice(0, 7)].reverse()
  const peak = Math.max(...week.map((entry) => entry.value ?? 0), 1)
  const fill = TINT_CLASS[tint] ?? 'bg-sys-blue'

  return (
    <div className="flex items-end gap-[3px]" style={{ height }} aria-hidden="true">
      {week.map((entry, index) => {
        const isToday = index === week.length - 1
        return (
          <div key={entry.day} className="flex h-full flex-1 flex-col justify-end">
            <div
              className={`w-full rounded-[2px] ${entry.value === null ? 'bg-ink-surface2' : fill} ${
                isToday || entry.value === null ? '' : 'opacity-55'
              }`}
              // Минимум 2 пиксела: иначе слабый день исчезает и читается как пропуск.
              style={{ height: `${Math.max(((entry.value ?? 0) / peak) * 100, 2)}%` }}
            />
          </div>
        )
      })}
    </div>
  )
}

/** Подписи дней недели под графиком — те же семь колонок. */
export function SparklineLegend({ days }: { days: MetricSummary['days'] }) {
  const week = [...days.slice(0, 7)].reverse()
  return (
    <div className="mt-1 flex gap-[3px]">
      {week.map((entry) => (
        <span key={entry.day} className="flex-1 text-center text-[10px] text-ink-3">
          {WEEKDAYS[new Date(`${entry.day}T12:00:00Z`).getUTCDay()]}
        </span>
      ))}
    </div>
  )
}
